import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FaInfoCircle } from 'react-icons/fa';
import { RewardProvider } from '../../types/reward.entity';
import { CreateRewardFormData } from '../../utils/reward.validation';
import { rewardProviderMeta } from '../../utils/provider-meta';
import { TremendousProductSelector } from './TremendousProductSelector';

const providerOptions: { id: RewardProvider, label: string, icon: React.ReactNode }[] = [
  {
    id: 'TREMENDOUS',
    label: rewardProviderMeta.TREMENDOUS.label,
    icon: <span className="text-fuchsia-600">{rewardProviderMeta.TREMENDOUS.icon}</span>,
  },
  {
    id: 'YAMANI_RESTURANT',
    label: rewardProviderMeta.YAMANI_RESTURANT.label,
    icon: <span className="text-red-500">{rewardProviderMeta.YAMANI_RESTURANT.icon}</span>,
  },
  {
    id: 'TAALY',
    label: rewardProviderMeta.TAALY.label,
    icon: <span className="text-purple-500">{rewardProviderMeta.TAALY.icon}</span>,
  },
];

export const RewardProviderFields: React.FC = () => {
  const { register, watch, reset, formState: { errors } } = useFormContext<CreateRewardFormData>();
  const providerName = watch('providerName');

  const handleProviderChange = (nextProvider: RewardProvider) => {
    if (providerName === nextProvider) return;

    reset({
      name: '',
      description: '',
      amount: 100,
      category: 'DIGITAL',
      isAvailable: true,
      providerName: nextProvider,
      amazonCurrency: 'USD',
      amazonAmount: 10,
      tremendousProduct: undefined,
      tremendousCurrencyCode: '',
      tremendousDenomination: undefined,
      countryList: [],
      expiresAt: '',
      mediaFiles: [],
    });
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
        <div className="border-b border-gray-100 pb-4 mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Step 1</p>
          <h3 className="mt-2 text-xl font-bold text-gray-800">Provider Integration</h3>
          <p className="mt-2 text-sm text-gray-500">
            Choose the provider first, then complete only the fields that apply for that reward source.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-6">
          {providerOptions.map(provider => (
            <button
              key={provider.id}
              type="button"
              onClick={() => handleProviderChange(provider.id)}
              className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                providerName === provider.id 
                  ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500 shadow-sm' 
                  : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3 w-full text-left">
                <div className="text-2xl shrink-0">{provider.icon}</div>
                <span className="text-sm font-semibold text-gray-700">{provider.label}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
          <h4 className="text-sm font-bold text-gray-700 mb-4 capitalize">{(providerName || '').toLowerCase()} Configuration</h4>
          
          {providerName === 'TREMENDOUS' ? (
            <TremendousProductSelector />
          ) : providerName === 'AMAZON' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-600 uppercase">Currency</label>
                <select
                  {...register('amazonCurrency')}
                  className="w-full p-2.5 rounded-lg border border-gray-300 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="AED">AED (د.إ)</option>
                </select>
                {errors.amazonCurrency && <p className="text-red-500 text-xs mt-1">{errors.amazonCurrency.message}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-600 uppercase">Gift Card Value</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">$</span>
                  <input
                    type="number"
                    min="1"
                    {...register('amazonAmount')}
                    className="w-full pl-8 p-2.5 rounded-lg border border-gray-300 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                {errors.amazonAmount && <p className="text-red-500 text-xs mt-1">{errors.amazonAmount.message}</p>}
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500 flex items-start gap-2">
              <FaInfoCircle className="mt-0.5 shrink-0" />
              <p>No extra provider-level configuration required for {providerName}.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
